import moment from 'moment';
import 'moment-timezone/builds/moment-timezone-with-data-10-year-range';
import { Moment } from 'moment';
import config from '../config';
import { Event } from '../types/Event/Event';

export const momentParse = (rawDate?: string | Date | Moment, timezone: string = config().universal.timezone): Moment => {
  let m: Moment;
  if (rawDate) {
    m = moment.utc(rawDate);
  } else {
    m = moment();
  }
  return m.tz(timezone);
};

export const dateAndTimeRange = (startAt: Moment, endAt?: Moment | null): string => {
  const dateFormat = 'dddd, MMMM D, YYYY';
  const timeFormat = 'h:mm a';
  const dateAndTimeFormat = `${dateFormat} ${timeFormat}`;
  const start = startAt.format(dateAndTimeFormat);

  // support events without end time
  if (!endAt || endAt.isSame(startAt)) {
    return start;
  }

  if (startAt.isSame(endAt, 'day')) {
    return `${start} – ${endAt.format(timeFormat)}`;
  }

  return `${start} – ${endAt.format(dateAndTimeFormat)}`;
};

export const eventDateAndTimeRange = (event: Event): string => {
  const { startAt, endAt, timezone } = event;
  return dateAndTimeRange(
    momentParse(startAt, timezone),
    endAt ? momentParse(endAt, timezone) : null,
  );
};

export const timeRange = (startAt: Moment, endAt: Moment | null, inlineDate: boolean = false): string => {
  const start = startAt.format('h:mm a');

  // support events without end time
  if (!endAt || endAt.isSame(startAt)) {
    return start;
  }

  const end = endAt.format('h:mm a');
  if (startAt.isSame(endAt, 'day')) {
    return `${start} – ${end}`;
  }

  const dateFormat = 'MMMM D, YYYY';
  if (inlineDate) {
    return `${start} (${startAt.format(dateFormat)}) – ${end} (${endAt.format(dateFormat)})`;
  }
  return `${start} – ${end} (${startAt.format(dateFormat)} – ${endAt.format(dateFormat)})`;
};

export const dateRange = (startAt: Moment, endAt: Moment | null): string => {
  if (!endAt || startAt.isSame(endAt, 'day')) {
    return startAt.format('MMMM D');
  }

  const start = `${startAt.format('MMMM D')}`;
  const end = endAt.format(startAt.isSame(endAt, 'month') ? 'D' : 'MMMM D');
  return `${start}-${end}`;
};

export const fromNow = (startAt: Moment, endAt: Moment): string => {
  const now = moment();
  if (startAt.diff(now) > 0) {
    if (now.isSame(startAt, 'day')) {
      return 'Today';
    } else if (now.add(1, 'day').isSame(startAt, 'day')) {
      return 'Tomorrow';
    }
    return `${startAt.fromNow(true)} from now`;
  } else if (endAt.diff(now) < 0) {
    return 'Already Happened';
  }
  return 'Happening Now';
};

export const combineDateAndTime = (
  date: string | Date | Moment | null,
  time: string | Date | Moment | null,
  timezone?: string,
): Moment | null => {
  if (!date || !time) {
    return null;
  }
  const momentDate = typeof date === 'string' ? moment(date, 'YYYY-MM-DD') : moment(date);
  const momentTime = typeof time === 'string' ? moment(time, 'HH:mm:ss') : moment(time);
  if (!momentDate.isValid() || !momentTime.isValid()) {
    return null;
  }
  const combined = moment(
    `${momentDate.format('YYYY-MM-DD')} ${momentTime.format('HH:mm:ss')}`
  );
  return timezone ? combined.tz(timezone, true) : combined;
}; 